<template>
  <div class="schema-reader">
    <div v-if="debug" class="schema-reader-debug">
      <div v-if="loading">Loading schema…</div>
      <div v-else-if="error" style="color:#b00020">{{ error }}</div>
      <pre v-else>{{ schema }}</pre>
    </div>

    <div v-if="!loading && !error && schema && schema.fields">
      <div class="fields-list">
        <div v-for="(field, key) in orderedFields" :key="key" class="field-row" :style="gridCellStyle(field)">
          <text-input v-if="field.label == 'serialNumber'" :name="key" :type="field.widget"
            :disabled="$session.get('privilege') != '0'" :label="$i18n.t(field.label)" :value="getValue(key, field)"
            :required="true" :infoText="field.infoText" @changeValue="(newValue) => updateProperty(newValue, key)" />

          <!-- text-like -->

          <text-input
            v-else-if="field.widget == 'text' || field.widget == 'color' || field.widget == 'number' || field.widget == 'decimal'"
            :name="key" :type="field.widget == 'decimal' ? 'number' : field.widget"
            :step="field.widget == 'decimal' ? 'any' : undefined" :label="$i18n.t(field.label)"
            :value="getValue(key, field)" :required="true" :infoText="field.infoText"
            @changeValue="(newValue) => updateProperty(newValue, key)" />

          <!-- select -->
          <select-input v-else-if="field.widget == 'select'" :name="key" :selected="getSelected(key, field)"
            :optionText="field.optionText" :label="$i18n.t(field.label)" :options="getOptionsSync(key, field)"
            @change="(newValue) => updateProperty(newValue, key)" />

          <!-- checkbox -->
          <checkbox v-else-if="field.widget == 'checkbox'" :name="key" :value="getValue(key, field)"
            :label="$i18n.t(field.label)" @change="(newValue) => updateProperty(newValue, key)" style="height: 100%;" />

          <!-- relation table-->
          <div v-else-if="field.widget == 'relation'" class="w-100 mb-4">
            <toggle-relation-table-button :id="field.id" :title="field.label" :items="getRelationOptions(key, field)"
              :insertedItems="getSelected(key, field)" @selectedItems="(newValue) => updateProperty(newValue, key)" />
          </div>
        </div>


      </div>
      <div class="mt-2 mb-2 w-100 d-flex justify-content-center">
        <div style="width: 30%; height: 2.5rem">
          <expandable-button :showCancel="element.id == 0" :buttons="buttons" @cancel="$emit('cancel')" />
        </div>
      </div>
    </div>


    <div v-else-if="!loading && !error" class="no-fields">No fields.</div>
    <confirm-delete-modal tableName="device" :name="element.name" @confirm="remove" />
  </div>
</template>

<script>
import Api from "@/api";

export default {
  name: "SchemaReader",
  props: {
    id: { type: Number, required: false, default: 0 },
    entity: { type: String, required: true },
    autoLoad: { type: Boolean, default: true },
    debug: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      error: null,
      schema: null,
      element: {
        id: 0,
        name: '',
        enable: true,
        kind: 'Telemetry',
        dynamicData: {}
      },
      // cache: key -> Array of options
      sources: {},
      currentBp: 'lg', // default; will be set on mount
    };
  },
  async created() {
    this.loading = true;
    this.service = new this[`$${this.entity}Service`]();
    this.element = new this[`$${this.entity}`]();
    this.$set(this.element, "dynamicData", {});


    if (this.id > 0) {
      const el = await this.service.search(this.id)
      this.element = el;
    }

    if (this.autoLoad) await this.load();

    this.updateBreakpoint();
    window.addEventListener('resize', this.updateBreakpoint, { passive: true });

    this.loading = false;


  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateBreakpoint);
  },
  watch: {
    entity() { if (this.autoLoad) this.load(); },
  },
  methods: {

    // --- NEW: breakpoint + span helpers ---
    updateBreakpoint() {
      const w = window.innerWidth || 1024;
      if (w >= 1440) this.currentBp = 'xl';
      else if (w >= 1024) this.currentBp = 'lg';
      else if (w >= 768) this.currentBp = 'md';
      else this.currentBp = 'sm';
    },

    resolveSpan(field) {
      const raw = field?.layout?.span;

      // Define per-breakpoint fallback widths
      const fallbackMap = {
        sm: 12,
        md: 6,
        lg: 4,
        xl: 4
      };

      if (typeof raw === 'number') {
        return Math.min(12, Math.max(1, raw));
      }

      if (raw && typeof raw === 'object') {
        // priority: exact bp → smaller bp → larger bp
        const bpOrder = ['sm', 'md', 'lg', 'xl'];
        const idx = bpOrder.indexOf(this.currentBp);

        // Try current or smaller breakpoints
        for (let i = idx; i >= 0; i--) {
          const v = raw[bpOrder[i]];
          if (typeof v === 'number') {
            return Math.min(12, Math.max(1, v));
          }
        }

        // Try larger breakpoints if nothing matched
        for (let i = idx + 1; i < bpOrder.length; i++) {
          const v = raw[bpOrder[i]];
          if (typeof v === 'number') {
            return Math.min(12, Math.max(1, v));
          }
        }
      }

      // fallback according to current breakpoint
      return fallbackMap[this.currentBp] ?? 12;
    },

    gridCellStyle(field) {
      const span = this.resolveSpan(field);
      return { gridColumn: `span ${span} / span ${span}` };
    },

    // ---------- api handling ----------


    getResponse(element) {
      return element?.value ?? element?.data?.value ?? null;
    },

    async update() {
      this.loading = true;
      await this.$nextTick();

      console.log(this.element);
      try {
        await this.service.update(this.element, this.id <= 0);
        this.$emit('updateElement', {
          value: this.element,
          update: false,
          element: {}
        });

      } catch (err) {
        console.log(err);
      }

      this.loading = false;
    },

    async remove() {
      this.loading = true;
      await this.$nextTick();

      await this.service
        .erase(this.element)
        .then(
          () => {
            this.$emit("removeElement", { row: this.row, value: this.element });
          },
          (err) => console.log(err)
        );

      this.loading = false;
    },

    async restore() {
      this.loading = true;
      await this.$nextTick();

      this.element.variables = null;
      this.element.enable = true;
      await this.service.update(this.element);
      this.$emit("removeElement", { row: this.row, value: this.element });

      this.loading = false;
    },
    // ---------- element handling ----------

    getValue(key, field) {
      if (key.includes("dynamic")) {
        if (!this.element.dynamicData) {
          this.$set(this.element, "dynamicData", {});
        }

        if (Object.prototype.hasOwnProperty.call(this.element.dynamicData, key)) {
          return this.element.dynamicData[key];
        }

        if (field.widget === "text") {
          this.$set(this.element.dynamicData, key, "");
        }

        if (field.widget === "checkbox") {
          this.$set(this.element.dynamicData, key, false);
        }

        if (field.widget === "decimal") {

          this.$set(this.element.dynamicData, key, 0);
        }


        return this.element.dynamicData[key];
      } else {

        if (Object.prototype.hasOwnProperty.call(this.element, key)) {
          return this.element[key];
        }

        if (field.widget === "text") {
          this.$set(this.element, key, "");
        }

        if (field.widget === "checkbox") {
          this.$set(this.element, key, false);
        }

        if (field.widget === "decimal") {
          this.$set(this.element, key, 0);
        }

      }



      return "";
    },

    isEmptyValue(v) {
      return v == null || v == undefined || v == "" || (
        typeof v === "object" &&
        !Array.isArray(v) &&
        Object.keys(v).length === 0
      );
    },

    matchesObjectOption(o, cur) {
      const fields = ["id", "value", "name", "label"];

      for (const field of fields) {
        const oHasField = Object.prototype.hasOwnProperty.call(o, field);
        const curHasField = Object.prototype.hasOwnProperty.call(cur, field);

        // Se um tem o campo e o outro não, já é diferente
        /*if (oHasField !== curHasField) {
          return false;
        }*/

        // Se tem o campo nos dois, precisa ser igual
        if (oHasField && curHasField) {
          if (String(o[field]) !== String(cur[field])) {
            return false;
          }
        }
      }

      // Se passou pelos 4 campos sem falhar, é o mesmo objeto
      return true;
    },

    findOption(cur, opts) {
      if (this.isEmptyValue(cur)) return null;

      // Caso o valor atual seja objeto
      if (typeof cur === "object") {
        const result = opts.find(o => o === cur || this.matchesObjectOption(o, cur));
        return result;
      }

      // Caso o valor atual seja string/number/bool
      return opts.find(o =>
        o === cur ||
        String(o?.id) === String(cur) ||
        String(o?.value) === String(cur) ||
        String(o?.name) === String(cur) ||
        String(o?.label) === String(cur)
      ) || cur;
    },

    getSelected(key, field) {
      const opts = this.getOptionsSync(key, field);
      const cur = this.element[key];



      if (key === "kind") {
        const elementType = this.element.kind;
        const match = this.findOption(elementType, opts);
        if (match) return match;

        this.updateProperty(opts[0] || {}, key);
        return opts[0] || {};
      }

      if (key === "dynamic_model") {
        const elementModel = this.element['dynamicData']['dynamic_model'];
        const match = this.findOption(elementModel, opts);
        if (match) return match;

        this.updateProperty(opts[0] || {}, key);
        return opts[0] || {};
      }

      if (key === "operator") {
        const elementOperator = this.element.operator;
        const match = opts.find(t => t.id === elementOperator);
        if (match) return match;

        this.updateProperty(opts[0] || {}, key);
        return opts[0] || {};
      }

      if (key.includes("dynamic_") && this.element.dynamicData) {
        const cur = this.element.dynamicData[key];
        console.log(key, field, cur);
        if (!this.isEmptyValue(cur)) {
          let result = this.findOption(cur, opts);
          if (result) return result;
        }

        this.updateProperty(opts[0] || {}, key);
        return opts[0] || {};
      }

      if (field.widget === "relation") {
        return [];
      }

      if (field.widget === "number") {
        return 0;
      }


      console.log(key, field, cur, this.isEmptyValue(cur));

      if (!this.isEmptyValue(cur)) {
        return this.findOption(cur, opts);
      }


      let result = this.findOption(cur, opts);
      if (!this.isEmptyValue(result)) {
        this.updateProperty(opts[0] || {}, key);
        return result;
      }


      // Mantido como você pediu:
      // se usuário não setou nada, usa primeira opção
      this.updateProperty(opts[0] || {}, key);
      return opts[0] || {};
    },

    async updateProperty(newValue, key) {


      if (key == "dynamic_deviceVersion") {
        let value = newValue?.value ?? newValue?.label ?? newValue;
        let oldValueDate = this.element['dynamicData'][key]?.value ?? this.element['dynamicData'][key]?.label ?? this.element['dynamicData'][key];
        let reload = value != oldValueDate;

        this.element['dynamicData'][key] = value;

        if (reload)
          await this.load();

        return;
      }

      if (key == 'dynamic_model') {
        if (!('dynamicData' in this.element)) this.element.dynamicData = {};

        let newValueData = newValue?.value ?? newValue?.label ?? newValue;
        let oldValueDate = this.element['dynamicData'][key]?.value ?? this.element['dynamicData'][key]?.label ?? this.element['dynamicData'][key];


        let reload = newValueData != oldValueDate;
        this.element['dynamicData'][key] = newValue;

        if (reload)
          await this.load();

      }


      if (key.includes('dynamic_')) {
        if (!('dynamicData' in this.element)) this.element.dynamicData = {};
        this.element['dynamicData'][key] = newValue;
        console.log('update', this.element)
        return;
      }

      if (key === "kind" || key == "model") {
        // Accept either full object or primitive event payloads
        if (key === "kind") this.element['model'] = "";
        let value = newValue?.value ?? newValue?.label ?? newValue;
        console.log(value, this.element[key])
        let reload = this.element[key] != value;

        this.element[key] = value;

        if (reload)
          await this.load();

        this.element['dynamicData'] = {};

        return;
      }



      if (key === "operator") {
        let value = newValue?.id;
        this.element[key] = value;
        return;
      }
      // If your select emits an object, store id/value; adjust to your <select-input> contract
      this.element[key] = newValue;
    },




    // ---------- options handling ----------

    getOptionsSync(key, field) {
      // Always return an array (no Promises in templates)
      if (this.sources[key]) return this.sources[key];
      if (field && field.options && Array.isArray(field.options.items)) {
        if (key === 'model' && this.element.kind) {
          return field.options.items.filter(option => option.kind == this.element.kind);
        }
        return field.options.items;
      }
      return []; // not loaded yet or no options
    },

    getRelationOptions(key, field) {
      if (this.sources[key]) {
        let source = this.sources[key]
        let insertedItems = this.getSelected(key, field);
        insertedItems = insertedItems.map(i => i.id)
        return source.filter(option => !insertedItems.includes(option.id));
      }

      return [];
    },

    async preloadOptions() {
      if (!this.schema || !this.schema.fields) return;

      const tasks = Object.entries(this.schema.fields)
        .filter(([, f]) => f && (f.widget === "select" || f.widget === "relation") && f.source && f.source.url)
        .map(async ([key, f]) => {
          try {
            const { data } = await Api.get(f.source.url);
            this.$set(this.sources, key, data);
          } catch (e) {
            this.$set(this.sources, key, []);
          }
        });

      await Promise.all(tasks);
    },



    // ---------- schema loading ----------
    async load() {
      this.loading = true;
      this.error = null;


      try {
        // Build query parameters dynamically
        const params = new URLSearchParams();

        if (this.element?.kind)
          params.append("kind", this.element.kind);

        console.log(this.element['dynamicData'])

        if (this.element['dynamicData']) {
          if (this.element['dynamicData']['dynamic_model'])
            params.append("model", this.element['dynamicData']['dynamic_model']['value']);

          if (this.element['dynamicData']['dynamic_version'])
            params.append("version", this.element['dynamicData']['dynamic_version']['value']);
        }


        const q = params.toString() ? `?${params.toString()}` : "";
        const url = `/api/ui/${encodeURIComponent(this.entity)}${q}`;


        const { data } = await Api.get(url);
        this.schema = data;

        await this.preloadOptions(); // <-- fetch remote options into sources
        this.$emit("loaded", data);

      } catch (e) {
        this.error = e?.message || "Failed to load schema";
        this.$emit("error", this.error);

      } finally {
        this.loading = false;
      }
    },

    reload() {
      return this.load();
    },

    showDeleteModal() {
      this.$bvModal.show("modalConfirmDelete");
    },

    hideModal() {
      this.$bvModal.hide("modalConfirmDelete");
    },

  },

  computed: {
    getSchema() { return this.schema; },
    buttons() {
      const list = [];
      if (this.element.id > 0) {
        if (this.element.enable) list.push({ type: "remove", label: "Desabilitar", onPress: this.showDeleteModal });
        else list.push({ type: "restore", label: "Restaurar", onPress: this.restore });
      }
      if (this.element.enable) list.push({ type: "upload", label: "Salvar", onPress: this.update });
      return list;
    },

    orderedFields() {
      return Object.entries(this.schema.fields)
        .sort(([, a], [, b]) => {
          // Se o widget for "relation", empurra para o final
          if (a.widget === "relation" && b.widget !== "relation") return 1;
          if (a.widget !== "relation" && b.widget === "relation") return -1;
          return 0;
        })
        .reduce((acc, [key, field]) => {
          acc[key] = field;
          return acc;
        }, {});
    }
  }

};
</script>

<style scoped>
.schema-reader {
  width: 100%;
}

.schema-reader-debug {
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
}

.fields-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 8px;
  align-items: start;
}

/* make each field fill its cell neatly */
.field-row {
  display: flex;
  flex-direction: column;
  min-width: 100%;
  width: auto;
  height: 100%;
  /* prevent overflow in narrow spans */
}

.field-key {
  width: 220px;
}

.field-label {
  opacity: 0.85;
}

.field-widget {
  color: #666;
}

.no-fields {
  opacity: 0.7;
  margin-top: 8px;
}
</style>
